import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { INSP_RESULTS, ONE_TIME_KINDS, previewNextDue } from "@/lib/inspectionConstants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

export default function CompleteDialog({ open, onClose, onDone, inspection }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [docCount, setDocCount] = useState(null);
  const [confirmNoProto, setConfirmNoProto] = useState(false);

  useEffect(() => {
    if (open) setForm({ actual_date: today, performed_by: "", service_provider: inspection?.service_provider || "",
      result: "bestanden", defects_found: "", measures: "", recommended: "", next_due: "", notes: "" });
  }, [open, inspection]); // eslint-disable-line

  useEffect(() => {
    if (open && inspection) {
      setConfirmNoProto(false); setDocCount(null);
      if (inspection.requires_protocol) api.get(`/inspections/${inspection.id}/documents`).then((r) => setDocCount(r.data.length)).catch(() => setDocCount(null));
    }
  }, [open, inspection]);

  const needsProto = !!inspection?.requires_protocol && docCount === 0;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isOneTime = inspection && ONE_TIME_KINDS.has(inspection.kind);
  const anchor = inspection?.next_due || form.actual_date;
  const preview = useMemo(
    () => (isOneTime ? null : previewNextDue(anchor, inspection?.interval_type, inspection?.interval_value, inspection?.interval_unit)),
    [anchor, inspection, isOneTime],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.actual_date) return toast.error("Prüfdatum ist erforderlich");
    setSaving(true);
    try {
      const r = await api.post(`/inspections/${inspection.id}/complete`, form);
      toast.success(r.data?.next_due ? `Abgeschlossen · nächster Termin ${r.data.next_due}` : "Abgeschlossen & archiviert");
      onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-600" />Prüfung / Wartung abschließen</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">{inspection?.title}</p>
        <form onSubmit={submit} className="space-y-4 mt-1" data-testid="complete-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tatsächliches Datum *"><Input type="date" value={form.actual_date || ""} onChange={(e) => set("actual_date", e.target.value)} data-testid="complete-date" /></Field>
            <Field label="Ergebnis">
              <Select value={form.result} onValueChange={(v) => set("result", v)}>
                <SelectTrigger data-testid="complete-result"><SelectValue /></SelectTrigger>
                <SelectContent>{INSP_RESULTS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Durchgeführt von"><Input value={form.performed_by || ""} onChange={(e) => set("performed_by", e.target.value)} data-testid="complete-by" /></Field>
            <Field label="Externer Prüfer / Dienstleister"><Input value={form.service_provider || ""} onChange={(e) => set("service_provider", e.target.value)} /></Field>
          </div>
          <Field label="Festgestellte Mängel"><Textarea value={form.defects_found || ""} onChange={(e) => set("defects_found", e.target.value)} rows={2} data-testid="complete-defects" /></Field>
          <Field label="Erforderliche Maßnahmen"><Textarea value={form.measures || ""} onChange={(e) => set("measures", e.target.value)} rows={2} /></Field>
          <Field label="Empfohlene Folgearbeiten"><Textarea value={form.recommended || ""} onChange={(e) => set("recommended", e.target.value)} rows={2} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nächster Termin (optional)"><Input type="date" value={form.next_due || ""} onChange={(e) => set("next_due", e.target.value)} data-testid="complete-next-due" /></Field>
            <div className="flex items-end text-xs text-blue-600 pb-2" data-testid="complete-next-preview">
              {isOneTime ? "Einmalig – kein Folgetermin" : (!form.next_due && preview ? `Berechnet: ${preview}` : "")}
            </div>
          </div>
          <Field label="Bemerkungen"><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} /></Field>
          {needsProto && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="protocol-warning">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-800">
                <p className="font-semibold">Prüfprotokoll fehlt</p>
                <p>Für diese Prüfung ist ein Prüfprotokoll erforderlich, es wurde aber noch kein Dokument hochgeladen.</p>
                <label className="flex items-center gap-2 mt-2 cursor-pointer"><Checkbox checked={confirmNoProto} onCheckedChange={setConfirmNoProto} data-testid="confirm-no-protocol" />Trotzdem ohne Protokoll abschließen</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={saving || (needsProto && !confirmNoProto)} className="bg-green-600 hover:bg-green-700" data-testid="confirm-complete-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Abschließen & archivieren"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
